/*
 * zeroLabs Teaser auf der Startseite (sections/zl-teaser.liquid). Geladen mit defer nur von der Sektion.
 *
 * Bedienung: Der Besucher wischt, der Browser rastet ein. Punkte und Pfeiltasten springen. Die Folien laufen im
 * Kreis. Mit "Automatisch weiter" kommt die nächste Folie von selbst, ein Balken im Punkt zeigt, wann.
 *
 * Effekte, jeder einzeln abschaltbar. Startwerte aus dem Theme-Editor (data-fx), mit ?zl-regler
 * erscheinen Regler auf der Seite, deren Werte im Browser gespeichert bleiben.
 * - Ankunft: Beim ersten Zeigen kommt das Bild von unten und schwingt nach (Feder).
 * - Text: Überschrift und Text steigen nacheinander auf. Preis und Button stehen sofort.
 * - Parallax: Beim Scrollen bleibt das Bild etwas zurück.
 * - Tiefe: Beim Wischen läuft das Bild langsamer als der Text.
 * - Einrasten: Nach dem Loslassen ein kleiner Ruck mit Nachschwingen.
 * - Neigen: Am Desktop neigt sich das Bild leicht zur Maus.
 * Folien "Grafik mit Text darüber" haben eigene Effekte zwischen Schrift und Grafik:
 * - Auftritt: Schrift und Grafik finden beim ersten Zeigen zueinander (vier Arten zum Vergleichen).
 * - Tiefe beim Scrollen: Grafik bleibt zurück, Schrift läuft voraus.
 * - Tiefe beim Wischen: Schrift läuft schneller als die Grafik.
 * - Maus: Schrift und Grafik weichen gegeneinander aus (Desktop).
 * In Ruhe stehen beide genau übereinander, damit die Schrift an ihrer Stelle in der Grafik sitzt.
 * Bei reduzierter Bewegung: keine Effekte, Springen ohne Gleiten.
 * Finger-Ereignisse werden nur gelesen, das Scrollen wird nie angehalten (docs/touch.md).
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOk = () => !reduced.matches;
  const STORE = 'zl-teaser-regler-2'; // neue Startwerte 03.10.2026, alte gespeicherte Regler gelten nicht mehr
  const SETTLE_WAIT = 120; // so lange ohne Scrollen gilt das Wischen als beendet (ms)
  const TILT_FOLLOW = 0.12; // Anteil pro Bild, mit dem die Flasche der Maus folgt
  const NEAR = 0.6; // so viel weniger als der Hintergrund bewegt sich eine stehende Flasche bei Parallax und Wischen
  const LAND_PER_SCALE = 1000; // Ankunft "aus der Nähe": 120 px Weg = 12 % größer
  const SLIDE_MS = 900; // Wechsel per Punkt oder Automatik: so lang gleitet der Streifen, gedämpft ohne Überschwingen
  const FADE_SHARE = 0.55; // Anteil der Dauer, in dem eine Zeile sichtbar wird, lang genug für ein weiches Einblenden

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  // Gedämpfte Feder von 0 nach 1. bounce 0 = ohne Überschwingen, 0,6 = deutlich.
  // Dauer = bis die Feder praktisch steht.
  const spring = (t, dur, bounce) => {
    if (t >= dur) return 1;
    if (t <= 0) return 0;
    const zeta = 1 - clamp(bounce, 0, 0.9);
    if (zeta >= 0.999) {
      const w = 6.6 / dur;
      return 1 - (1 + w * t) * Math.exp(-w * t);
    }
    const w0 = 4.6 / (zeta * dur);
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    const e = Math.exp(-zeta * w0 * t);
    return 1 - e * (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t));
  };

  const readStore = () => {
    try { return JSON.parse(localStorage.getItem(STORE) || 'null'); } catch (e) { return null; }
  };
  const writeStore = (v) => {
    try {
      if (v) localStorage.setItem(STORE, JSON.stringify(v));
      else localStorage.removeItem(STORE);
    } catch (e) { /* ohne Speicher gelten die Werte nur bis zum Neuladen */ }
  };
  const merge = (base, over) => {
    const out = JSON.parse(JSON.stringify(base));
    if (!over) return out;
    Object.keys(out).forEach((k) => { if (over[k]) Object.assign(out[k], over[k]); });
    return out;
  };

  const init = (root) => {
    if (!root || root.dataset.zltReady) return;
    root.dataset.zltReady = '1';
    try {
      setup(root);
    } catch (e) {
      // Bricht etwas ab, bleibt kein Text versteckt
      root.classList.remove('zlt-fx');
      root.querySelectorAll('[data-zlt-layer]').forEach((l) => { l.style.transform = ''; });
    }
  };

  const setup = (root) => {

    const track = root.querySelector('[data-zlt-track]');
    const slides = [...root.querySelectorAll('[data-zlt-slide]')];
    const dots = [...root.querySelectorAll('[data-zlt-dot]')];
    if (!track || !slides.length) return;

    let base = {};
    try { base = JSON.parse(root.dataset.fx || '{}'); } catch (e) { base = {}; }
    const withRegler = /[?&]zl-regler/.test(window.location.search);
    let cfg = merge(base, withRegler ? readStore() : null);
    // Das Vorbereitungs-Skript hat .zlt-fx nur gesetzt, wenn Bewegung erlaubt war und es rechtzeitig lief
    const fxReady = root.classList.contains('zlt-fx');

    const state = slides.map((el) => ({
      el,
      fx: el.hasAttribute('data-zlt-fx'),
      layer: el.querySelector('[data-zlt-layer]'),
      bottle: el.querySelector('[data-zlt-bottle]'),
      shadow: el.querySelector('[data-zlt-shadow]'),
      shadowBase: Number(getComputedStyle(el).getPropertyValue('--t-shadow')) || 0,
      lie: el.dataset.zltScene === 'lie',
      dir: el.dataset.zltDir || 'up',
      img: el.querySelector('.zl-teaser__bottle-img') || el.querySelector('.zl-teaser__image'),
      ins: [...el.querySelectorAll('[data-zlt-in]')],
      shown: false,
      arrive: null,
      settle: null,
      tilt: { x: 0, y: 0, tx: 0, ty: 0 },
      ov: el.classList.contains('zl-teaser__slide--overlay'),
      ovImg: el.querySelector('.zl-ov__img'),
      ovTxts: [...el.querySelectorAll('[data-zlt-ovtxt]')],
      ovIn: null,
      ovMouse: { x: 0, y: 0, tx: 0, ty: 0 },
    }));

    // ---------- Bedienung ----------
    // Endlos: Die Folien laufen im Kreis. Ab drei Folien steht die gezeigte immer in der Mitte des Streifens,
    // nach dem Einrasten wandert die äußerste Folie unsichtbar auf die andere Seite. So geht Wischen in beide
    // Richtungen immer weiter, auch vom letzten zum ersten Teaser.
    const n = slides.length;
    const loop = n >= 3;
    track.style.scrollBehavior = 'auto';
    const width = () => Math.max(track.clientWidth, 1);
    const inDom = () => [...track.children].filter((el) => el.hasAttribute('data-zlt-slide'));
    const posOf = (el) => inDom().indexOf(el);
    const posNow = () => clamp(Math.round(track.scrollLeft / width()), 0, n - 1);
    const current = () => slides.indexOf(inDom()[posNow()]);
    let pending = null; // Folie, zu der gerade gefahren wird
    const basis = () => (pending === null ? current() : pending);

    // Wechsel per Punkt, Taste oder Automatik: gedämpft ohne Überschwingen
    let anim = 0;
    const stopAnim = () => {
      if (!anim) return;
      cancelAnimationFrame(anim);
      anim = 0;
      track.style.scrollSnapType = '';
    };
    const scrollToPos = (pos) => {
      const to = pos * width();
      stopAnim();
      if (!motionOk()) { track.scrollLeft = to; return; }
      const from = track.scrollLeft;
      const t0 = performance.now();
      track.style.scrollSnapType = 'none';
      const step = (now) => {
        const t = now - t0;
        track.scrollLeft = from + (to - from) * spring(t, SLIDE_MS, 0);
        if (t < SLIDE_MS) { anim = requestAnimationFrame(step); return; }
        anim = 0;
        track.scrollLeft = to;
        track.style.scrollSnapType = '';
      };
      anim = requestAnimationFrame(step);
    };
    const go = (i) => {
      pending = ((i % n) + n) % n;
      scrollToPos(posOf(slides[pending]));
    };
    // Umstellen ohne Einrasten des Browsers dazwischen, danach steht die gezeigte Folie genau wie vorher
    const reorder = (change, shown) => {
      track.style.scrollSnapType = 'none';
      change();
      track.scrollLeft = posOf(shown) * width();
      requestAnimationFrame(() => requestAnimationFrame(() => { if (!anim) track.style.scrollSnapType = ''; }));
    };
    // Steht die gezeigte Folie am Rand des Streifens, wandert die gegenüberliegende auf diese Seite
    const rotate = () => {
      if (!loop || anim) return;
      const list = inDom();
      const pos = posNow();
      if (pos !== 0 && pos !== n - 1) return;
      reorder(() => {
        if (pos === 0) track.insertBefore(list[n - 1], list[0]);
        else track.appendChild(list[0]);
      }, list[pos]);
    };

    // Automatisch weiter: Der Balken im Punkt läuft ab, dann kommt die nächste Folie. Angehalten wird,
    // solange ein Finger auf dem Teaser liegt, der Teaser nicht zu sehen ist, das Fenster im Hintergrund ist,
    // die Tastatur im Teaser ist oder jemand auf Anhalten tippt.
    const autoSec = Number(root.dataset.autoplay) || 0;
    const autoOn = () => autoSec > 0 && motionOk();
    const pauseBtn = root.querySelector('[data-zlt-pause]');
    const reasons = new Set();
    const syncPause = () => {
      root.classList.toggle('zlt-paused', reasons.size > 0);
      root.classList.toggle('zlt-user-paused', reasons.has('user'));
      if (pauseBtn) pauseBtn.setAttribute('aria-label', reasons.has('user') ? 'Teaser abspielen' : 'Teaser anhalten');
    };
    // Der Balken wird Bild für Bild aus der vergangenen Zeit gesetzt (--p von 0 bis 1). So beginnt er sicher leer,
    // steht beim Anhalten genau still und läuft danach an derselben Stelle weiter, in jedem Browser gleich.
    let prog = 0;
    let lastTick = 0;
    let barRaf = 0;
    const paintBar = () => {
      const i = basis();
      dots.forEach((dot, k) => dot.style.setProperty('--p', k === i ? Math.min(prog, 1).toFixed(4) : '0'));
    };
    const tick = (now) => {
      barRaf = 0;
      if (!autoOn() || reasons.size) return;
      if (!anim) prog += (now - lastTick) / (autoSec * 1000); // während des Gleitens wartet die Zeit
      lastTick = now;
      paintBar();
      if (prog >= 1) {
        prog = 0;
        go(basis() + 1);
      }
      barRaf = requestAnimationFrame(tick);
    };
    const runBar = () => {
      if (barRaf || !autoOn() || reasons.size) return;
      lastTick = performance.now();
      barRaf = requestAnimationFrame(tick);
    };
    const stopBar = () => {
      if (barRaf) cancelAnimationFrame(barRaf);
      barRaf = 0;
    };
    const pause = (why) => { reasons.add(why); syncPause(); stopBar(); };
    const resume = (why) => { reasons.delete(why); syncPause(); runBar(); };
    const restartBar = () => {
      root.classList.toggle('zlt-auto', autoOn());
      prog = 0;
      paintBar();
      if (autoOn()) runBar(); else stopBar();
    };
    if (pauseBtn) pauseBtn.addEventListener('click', () => (reasons.has('user') ? resume('user') : pause('user')));
    document.addEventListener('visibilitychange', () => (document.hidden ? pause('hidden') : resume('hidden')));
    root.addEventListener('focusin', (event) => { if (event.target.matches(':focus-visible')) pause('focus'); });
    root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) resume('focus'); });

    let shownIdx = -1;
    const updateNav = () => {
      if (pending !== null && anim === 0 && Math.abs(track.scrollLeft - posOf(slides[pending]) * width()) < 2) pending = null;
      const i = pending === null ? current() : pending;
      dots.forEach((dot, k) => {
        dot.classList.toggle('is-active', k === i);
        if (k === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (i !== shownIdx) { shownIdx = i; restartBar(); }
    };
    dots.forEach((dot, k) => dot.addEventListener('click', () => go(k)));
    root.addEventListener('keydown', (event) => {
      if (event.target.closest('input, textarea, select')) return;
      if (event.key === 'ArrowLeft') { go(basis() - 1); event.preventDefault(); }
      if (event.key === 'ArrowRight') { go(basis() + 1); event.preventDefault(); }
    });
    root.addEventListener('shopify:block:select', (event) => {
      const n = slides.indexOf(event.target);
      if (n > -1) go(n);
    });

    // ---------- Effekte ----------
    const fxOn = () => fxReady && motionOk();
    let inView = true;
    let lastScrollLeft = track.scrollLeft;
    let swipeDir = 0;
    let userSwipe = false;
    let settleTimer = 0;
    let raf = 0;

    const applyVars = () => {
      root.style.setProperty('--zlt-arrive-dist', `${cfg.arrive ? cfg.arrive.dist : 0}px`);
      root.style.setProperty('--zlt-text-dist', `${cfg.text ? cfg.text.dist : 0}px`);
      root.style.setProperty('--zlt-land-scale', String(1 + (cfg.arrive ? cfg.arrive.dist : 0) / LAND_PER_SCALE));
      // Richtung aus den Reglern überschreibt die Einstellung der Folie
      state.forEach((st) => {
        const d = cfg.arrive && cfg.arrive.dir ? cfg.arrive.dir : st.el.dataset.zltDir || 'up';
        st.dir = d;
        st.el.setAttribute('data-zlt-dir-now', d);
      });
      root.toggleAttribute('data-fx-arrive', !!(cfg.arrive && cfg.arrive.on));
      root.toggleAttribute('data-fx-text', !!(cfg.text && cfg.text.on));
      if (cfg.ovin && cfg.ovin.on) root.setAttribute('data-fx-ovin', cfg.ovin.style || 'rise');
      else root.removeAttribute('data-fx-ovin');
      root.style.setProperty('--zlt-ovin-zoom', String(1 + (cfg.ovin ? cfg.ovin.zoom : 0) / 100));
    };

    // ---------- Grafik mit Text ----------
    // "Beschlagen": so unscharf startet die Grafik, wie Kondenswasser auf Glas (zl-tokens.css)
    const fogBlur = parseFloat(getComputedStyle(root).getPropertyValue('--zl-teaser-fog')) || 0;
    const ovChildren = (s) => s.ovTxts.flatMap((t) => [...t.children]);
    const ovItems = (s) => ovChildren(s).filter((e) => getComputedStyle(e).display !== 'none');
    const ovClearItems = (s) => {
      ovChildren(s).forEach((e) => { e.style.translate = ''; e.style.scale = ''; e.style.opacity = ''; });
    };
    const ovSetTxt = (s, v) => { s.ovTxts.forEach((t) => { t.style.transform = v; }); };
    // Auftritt: Wert je Zeile und für die Grafik zum Zeitpunkt t (ms seit Start)
    const ovFrame = (s, t) => {
      const c = cfg.ovin;
      const a = s.ovIn;
      const dur = c.dur;
      const lead = c.style === 'fog' ? dur * 0.35 : 0; // "Beschlagen": erst wird die Grafik klar, dann kommt die Schrift
      let busy = false;
      a.items.forEach((e, n) => {
        const ti = t - lead - n * c.step;
        const p = spring(ti, dur, c.bounce / 100);
        const o = clamp(ti / (dur * FADE_SHARE), 0, 1);
        const r = 1 - p;
        let dx = 0;
        let dy = c.dist * r;
        let sc = 1;
        if (c.style === 'depth') { dy = c.dist * 0.3 * r; sc = 1 - (c.zoom / 100) * 1.5 * r; }
        if (c.style === 'slide' && a.left[n]) { dx = -c.dist * r; dy = 0; }
        e.style.translate = `${dx.toFixed(1)}px ${dy.toFixed(1)}px`;
        e.style.scale = sc === 1 ? '' : sc.toFixed(4);
        e.style.opacity = o.toFixed(3);
        if (ti < dur) busy = true;
      });
      const pi = spring(t, dur * 1.3, c.bounce / 200);
      a.zoom = (c.zoom / 100) * (1 - pi);
      a.blur = c.style === 'fog' ? fogBlur * (1 - clamp(t / (dur * 0.9), 0, 1)) ** 2 : 0;
      if (t < dur * 1.3) busy = true;
      if (!busy) { ovClearItems(s); s.ovIn = null; }
      return busy;
    };
    const ovStart = (s) => {
      if (!s.ov || !s.fx || !s.ovTxts.length || !(cfg.ovin && cfg.ovin.on) || !fxOn()) return;
      const items = ovItems(s);
      // Mittig gesetzte Folien kommen auch bei "von der Seite" von unten, sonst kippt die Achse
      const left = getComputedStyle(s.el).getPropertyValue('--ta').trim() !== 'center';
      s.ovIn = { t0: performance.now(), items, left: items.map(() => left), zoom: 0, blur: 0 };
      ovFrame(s, 0); // Ausgangslage sofort, damit nichts aufblitzt
      renderOverlay(s, s.ovIn.t0, width());
      kick();
    };


    const imageReady = (s) => new Promise((resolve) => {
      if (!s.img || s.img.complete) { resolve(); return; }
      const done = () => resolve();
      s.img.addEventListener('load', done, { once: true });
      s.img.addEventListener('error', done, { once: true });
      setTimeout(done, 1500);
    });

    // Text sofort, das Bild kommt, sobald es geladen ist (höchstens 1,5 s Warten)
    const show = (s) => {
      if (s.shown) return;
      s.shown = true;
      ovStart(s);
      if (cfg.text && cfg.text.on) {
        s.ins.forEach((el, n) => { el.style.transitionDelay = `${n * cfg.text.step}ms`; });
      }
      s.el.classList.add('zlt-shown');
      const token = (s.token = (s.token || 0) + 1);
      imageReady(s).then(() => {
        if (token !== s.token || !s.shown) return;
        if (cfg.arrive && cfg.arrive.on && fxOn()) {
          s.arrive = { t0: performance.now(), from: cfg.arrive.dist };
        }
        s.arrived = true;
        s.el.classList.add('zlt-arrived');
        kick();
      });
    };

    const replay = (s) => {
      s.shown = false;
      s.arrived = false;
      s.arrive = null;
      s.ovIn = null;
      ovClearItems(s);
      s.el.classList.remove('zlt-shown', 'zlt-arrived');
      s.ins.forEach((el) => { el.style.transitionDelay = '0ms'; });
      render();
      // zwei Bilder warten, damit der Browser den Ausgangszustand sieht
      requestAnimationFrame(() => requestAnimationFrame(() => show(s)));
    };

    // Bewegung nur auf Folien mit Effekten und Flasche.
    // Steht: Die Flasche ist eine eigene Ebene vor dem Hintergrund. Ankunft, Einrasten und Maus bewegen nur sie,
    //   Parallax und Wischen bewegen den Hintergrund mehr als die Flasche (sie ist näher).
    // Liegt: Die Flasche liegt genau auf der Flasche im Foto, der echte Schatten ist im Foto. Sie bewegt sich
    //   mit dem Foto und darf es nie freilegen. Ankunft "aus der Nähe": größer, mit weichem Schatten, legt sich ab.
    const render = () => {
      raf = 0;
      if (!fxOn()) {
        state.forEach((s) => {
          if (s.ovImg) { s.ovImg.style.transform = ''; s.ovImg.style.filter = ''; }
          ovSetTxt(s, '');
          ovClearItems(s);
          if (s.layer) s.layer.style.transform = '';
          if (s.bottle) { s.bottle.style.transform = ''; s.bottle.style.filter = ''; }
          if (s.shadow) s.shadow.style.cssText = '';
        });
        return;
      }
      const now = performance.now();
      const w = width();
      const rect = root.getBoundingClientRect();
      const past = clamp(-rect.top, 0, rect.height);
      const dist = cfg.arrive ? cfg.arrive.dist : 0;
      let busy = false;

      state.forEach((s) => {
        if (s.ov && s.ovImg && s.ovTxts.length) {
          if (renderOverlay(s, now, w)) busy = true;
          return;
        }
        if (!s.fx || !s.bottle) {
          if (s.layer) s.layer.style.transform = '';
          return;
        }
        let bx = 0; // Flasche, in der Ebene des Bilds
        let by = 0;
        let bs = 1;
        let gx = 0; // Hintergrund samt Flasche
        let gy = 0;
        let gs = 1;
        let k = 0; // wie weit die Flasche noch unterwegs ist, 0 = gelandet

        // Ankunft
        if (cfg.arrive && cfg.arrive.on) {
          if (!s.arrived) {
            k = 1;
          } else if (s.arrive) {
            const t = now - s.arrive.t0;
            k = 1 - spring(t, cfg.arrive.dur, cfg.arrive.bounce / 100);
            if (t >= cfg.arrive.dur) { s.arrive = null; k = 0; } else busy = true;
          }
          if (s.dir === 'land') bs += (dist / LAND_PER_SCALE) * k;
          else if (s.dir === 'drop') by -= dist * k;
          else by += dist * k;
        }

        // Parallax: Hintergrund bleibt beim Scrollen zurück, Reserve oben über Vergrößern von unten
        if (cfg.parallax && cfg.parallax.on) {
          const a = cfg.parallax.amount / 100;
          gs += a / 2;
          // nie weiter, als die Vergrößerung oben Reserve lässt, sonst wird der Rand sichtbar
          const reserve = ((s.layer.offsetHeight || 0) * (gs - 1)) / 2;
          gy += Math.min(past * a * 0.5, reserve);
        }

        // Tiefe beim Wischen: Hintergrund läuft langsamer als Text
        if (cfg.depth && cfg.depth.on) {
          const o = clamp((s.el.offsetLeft - track.scrollLeft) / w, -1, 1);
          gx += -o * w * (cfg.depth.amount / 100);
        }

        if (!s.lie) {
          // Die stehende Flasche ist näher als der Hintergrund: Sie macht Parallax und Wischen nur zum Teil mit
          bx -= gx * NEAR;
          by -= gy * NEAR;

          if (s.settle) {
            const t = now - s.settle.t0;
            const dur = Math.max(cfg.arrive ? cfg.arrive.dur * 0.6 : 600, 300);
            bx += s.settle.from * (1 - spring(t, dur, cfg.settle.bounce / 100));
            if (t >= dur) s.settle = null; else busy = true;
          }

          if (cfg.tilt && cfg.tilt.on) {
            const tl = s.tilt;
            tl.x += (tl.tx - tl.x) * TILT_FOLLOW;
            tl.y += (tl.ty - tl.y) * TILT_FOLLOW;
            if (Math.abs(tl.tx - tl.x) > 0.05 || Math.abs(tl.ty - tl.y) > 0.05) busy = true;
            bx += tl.x;
            by += tl.y * 0.3;
            gx -= tl.x * 0.5;
            gs += 0.02;
          }
        } else {
          s.settle = null;
        }

        s.bottle.style.transform = bs !== 1
          ? `translate3d(${bx.toFixed(1)}px, ${by.toFixed(1)}px, 0) scale(${bs.toFixed(4)})`
          : `translate3d(${bx.toFixed(1)}px, ${by.toFixed(1)}px, 0)`;
        if (s.layer) s.layer.style.transform = `translate3d(${gx.toFixed(1)}px, ${gy.toFixed(1)}px, 0) scale(${gs.toFixed(4)})`;

        if (s.lie) {
          // Schatten im Flug: weich, nach rechts unten (Licht von links oben), verschwindet beim Ablegen
          const h = s.bottle.offsetHeight || 1;
          s.bottle.style.filter = k > 0.002
            ? /* token-ok: Schatten im Flug, Stärke wächst mit der Höhe */ `drop-shadow(${(h * 0.03 * k).toFixed(1)}px ${(h * 0.045 * k).toFixed(1)}px ${(h * 0.03 * k + 1).toFixed(1)}px rgba(0, 0, 0, ${(0.35 * Math.min(1, k * 3)).toFixed(3)}))`
            : '';
        } else if (s.shadow) {
          // Kontaktschatten am Boden: voll, wenn die Flasche steht, blass und schmal, solange sie fehlt oder schwebt
          const air = s.dir === 'drop' ? k : Math.min(1, k * 1.5);
          s.shadow.style.opacity = (s.shadowBase * (1 - air)).toFixed(3);
          s.shadow.style.transform = `translateX(${(bx * 0.9).toFixed(1)}px) scaleX(${(1 - 0.35 * air).toFixed(3)})`;
        }
      });

      if (busy) kick();
    };

    // Grafik mit Text: In Ruhe stehen Grafik und Schrift genau übereinander. Die Grafik wird nur so weit
    // vergrößert, wie eine Verschiebung Rand freilegen würde, und ist in Ruhe wieder genau 1.
    function renderOverlay(s, now, w) {
      if (!s.fx) {
        s.ovImg.style.transform = '';
        s.ovImg.style.filter = '';
        ovSetTxt(s, '');
        return false;
      }
      let busy = false;
      let gx = 0;
      let gy = 0;
      let tx = 0;
      let ty = 0;
      let zoom = 0;
      let blur = 0;
      const fw = s.ovImg.offsetWidth || 1;
      const fh = s.ovImg.offsetHeight || 1;

      if (s.ovIn) {
        if (ovFrame(s, now - s.ovIn.t0)) busy = true;
        if (s.ovIn) { zoom = s.ovIn.zoom; blur = s.ovIn.blur; }
      }

      if (cfg.ovscroll && cfg.ovscroll.on) {
        const r = s.el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // Ruhelage: Steht der Teaser schon beim Laden im Bild, so wie beim Laden, sonst in der Bildschirmmitte.
        // So passt die Schrift in Ruhe genau auf die Grafik.
        const docTop = r.top + window.scrollY;
        const rest = docTop < vh ? docTop + r.height / 2 - vh / 2 : 0;
        const off = clamp(r.top + r.height / 2 - vh / 2 - rest, -(vh + r.height) / 2, (vh + r.height) / 2);
        // Weich: Der Wert folgt dem Scrollen gedämpft und läuft nach dem Anhalten sanft aus
        const follow = 1 - clamp(cfg.ovscroll.soft || 0, 0, 95) / 100;
        if (s.ovOff === undefined) s.ovOff = off;
        s.ovOff += (off - s.ovOff) * follow;
        if (Math.abs(off - s.ovOff) > 0.3) busy = true; else s.ovOff = off;
        gy += -s.ovOff * (cfg.ovscroll.img / 100);
        ty += s.ovOff * (cfg.ovscroll.txt / 100);
      }

      if (cfg.ovswipe && cfg.ovswipe.on) {
        const o = clamp((s.el.offsetLeft - track.scrollLeft) / w, -1, 1);
        tx += o * w * (cfg.ovswipe.txt / 100);
      }

      if (cfg.ovmouse && cfg.ovmouse.on) {
        const m = s.ovMouse;
        m.x += (m.tx - m.x) * TILT_FOLLOW;
        m.y += (m.ty - m.y) * TILT_FOLLOW;
        if (Math.abs(m.tx - m.x) > 0.05 || Math.abs(m.ty - m.y) > 0.05) busy = true;
        tx += m.x;
        ty += m.y * 0.5;
        gx -= m.x * 0.6;
        gy -= m.y * 0.3;
      }

      const cover = 1 + 2 * Math.max(Math.abs(gx) / fw, Math.abs(gy) / fh);
      const gs = Math.max(1 + zoom, cover);
      s.ovImg.style.transform = gx || gy || gs !== 1
        ? `translate3d(${gx.toFixed(1)}px, ${gy.toFixed(1)}px, 0) scale(${gs.toFixed(4)})`
        : '';
      s.ovImg.style.filter = blur > 0.05 ? /* token-ok: Unschärfe beim Auftritt "Beschlagen" */ `blur(${blur.toFixed(2)}px)` : '';
      ovSetTxt(s, tx || ty ? `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0)` : '');
      return busy;
    }

    const kick = () => {
      if (!raf) raf = requestAnimationFrame(render);
    };

    // Welche Folie gilt als gezeigt: mindestens halb im Bild, und der Teaser ist sichtbar
    const checkShown = () => {
      if (!inView) return;
      const w = width();
      const again = cfg.ovin && cfg.ovin.on && cfg.ovin.again && fxOn();
      state.forEach((s) => {
        const o = Math.abs(s.el.offsetLeft - track.scrollLeft) / w;
        if (o < 0.5 && !s.shown) show(s);
        // "Jedes Mal": Ist eine Folie ganz aus dem Bild gewischt, steht sie wieder am Anfang und tritt beim nächsten Mal neu auf
        else if (again && s.ov && s.shown && o > 0.98) {
          s.shown = false;
          s.ovIn = null;
          ovClearItems(s);
          s.el.classList.remove('zlt-shown');
        }
      });
    };

    const onSettled = () => {
      if (anim) return;
      pending = null;
      rotate();
      updateNav();
      if (userSwipe) restartBar(); // nach Wischen läuft die Zeit für die gezeigte Folie von vorn
      if (userSwipe && cfg.settle && cfg.settle.on && fxOn() && swipeDir) {
        const s = state[current()];
        s.settle = { t0: performance.now(), from: swipeDir * cfg.settle.dist };
        kick();
      }
      userSwipe = false;
      swipeDir = 0;
    };

    track.addEventListener('scroll', () => {
      const d = track.scrollLeft - lastScrollLeft;
      if (Math.abs(d) > 0.5) swipeDir = d > 0 ? -1 : 1;
      lastScrollLeft = track.scrollLeft;
      updateNav();
      checkShown();
      kick();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettled, SETTLE_WAIT);
    }, { passive: true });
    ['pointerdown', 'wheel', 'touchstart'].forEach((type) => track.addEventListener(type, () => {
      stopAnim();
      pending = null;
      userSwipe = true;
      if (type !== 'wheel') pause('touch');
    }, { passive: true }));
    ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach((type) => window.addEventListener(type, () => resume('touch'), { passive: true }));

    window.addEventListener('scroll', () => { if (inView) kick(); }, { passive: true });
    window.addEventListener('resize', kick, { passive: true });

    // Platz über dem Teaser (Kopf), damit er am Desktop in den sichtbaren Bildschirm passt (zl-teaser.css, --above)
    const html = document.documentElement;
    const under = html.classList.contains('zlt-under');
    const head = document.querySelector('.header-wrapper');
    const measureAbove = () => {
      // Vollbild: Der Teaser liegt unter dem Kopf, gemessen wird der Kopf selbst
      if (under && head) {
        html.style.setProperty('--zlt-head', `${head.offsetHeight}px`);
        root.style.setProperty('--zlt-above', `${head.offsetHeight}px`);
        return;
      }
      const top = root.getBoundingClientRect().top + window.scrollY;
      if (top < window.innerHeight) root.style.setProperty('--zlt-above', `${Math.round(top)}px`);
      else root.style.removeProperty('--zlt-above');
    };
    // Kopf wird weiß, sobald gescrollt wird, damit Text nie unter Logo und Menü durchläuft
    if (under) {
      const solid = () => html.classList.toggle('zlt-under-solid', window.scrollY > 2);
      solid();
      window.addEventListener('scroll', solid, { passive: true });
    }
    measureAbove();
    window.addEventListener('resize', measureAbove, { passive: true });
    window.addEventListener('load', measureAbove, { once: true });

    root.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const s = state[current()];
      if (s.ov) {
        if (!cfg.ovmouse || !cfg.ovmouse.on) return;
        const r = s.el.getBoundingClientRect();
        s.ovMouse.tx = ((event.clientX - r.left) / r.width - 0.5) * 2 * cfg.ovmouse.px;
        s.ovMouse.ty = ((event.clientY - r.top) / r.height - 0.5) * 2 * cfg.ovmouse.px;
        kick();
        return;
      }
      if (!cfg.tilt || !cfg.tilt.on) return;
      const r = s.el.getBoundingClientRect();
      const px = (event.clientX - r.left) / r.width - 0.5;
      const py = (event.clientY - r.top) / r.height - 0.5;
      s.tilt.tx = px * 2 * cfg.tilt.deg;
      s.tilt.ty = py * 2 * cfg.tilt.deg;
      kick();
    }, { passive: true });
    root.addEventListener('pointerleave', () => {
      state.forEach((s) => { s.tilt.tx = 0; s.tilt.ty = 0; s.ovMouse.tx = 0; s.ovMouse.ty = 0; });
      kick();
    });

    if ('IntersectionObserver' in window) {
      let wasOut = false;
      new IntersectionObserver((entries) => {
        inView = entries.some((e) => e.isIntersecting);
        if (inView) resume('view'); else pause('view');
        if (!inView) wasOut = true;
        // "Bei jedem Hineinscrollen": nach dem Verlassen spielt die gezeigte Folie ihren Auftritt neu
        if (inView && wasOut && cfg.ovin && cfg.ovin.on && cfg.ovin.again && fxOn()) {
          const s = state[current()];
          if (s.ov && s.shown) { wasOut = false; replay(s); return; }
        }
        if (inView) { wasOut = false; checkShown(); kick(); }
      }, { threshold: 0.25 }).observe(root);
    }

    reduced.addEventListener('change', () => {
      restartBar();
      if (!motionOk()) state.forEach((s) => { s.shown = true; s.arrived = true; s.el.classList.add('zlt-shown', 'zlt-arrived'); });
      kick();
    });

    // Ohne Effekte (oder Skript zu spät): alles sofort zeigen
    if (!fxOn()) state.forEach((s) => { s.shown = true; s.arrived = true; s.el.classList.add('zlt-shown', 'zlt-arrived'); });

    // Erste Folie in die Mitte, damit man auch nach links wischen kann
    if (loop) {
      const list = inDom();
      reorder(() => track.insertBefore(list[n - 1], list[0]), list[0]);
      lastScrollLeft = track.scrollLeft;
    }
    applyVars();
    updateNav();
    restartBar();
    render();
    checkShown();

    if (withRegler) buildRegler(root, () => cfg, (next, opts) => {
      cfg = next;
      applyVars();
      if (opts && opts.replay) replay(state[current()]);
      kick();
    }, base, () => replay(state[current()]));
  };

  // ---------- Regler zum Ausprobieren ----------
  const CONTROLS = [
    { g: 'arrive', name: 'Ankunft', hint: 'Flasche kommt von unten', rows: [
      ['dist', 'Weg', 0, 300, 10, 'px'], ['dur', 'Dauer', 300, 2500, 50, 'ms'], ['bounce', 'Nachschwingen', 0, 60, 5, '%']] },
    { g: 'text', name: 'Text', hint: 'steigt nacheinander auf', rows: [
      ['step', 'Abstand', 0, 300, 10, 'ms'], ['dist', 'Weg', 0, 60, 2, 'px']] },
    { g: 'parallax', name: 'Parallax', hint: 'Hintergrund bleibt beim Scrollen zurück', rows: [
      ['amount', 'Stärke', 0, 50, 1, '%']] },
    { g: 'depth', name: 'Tiefe', hint: 'Hintergrund läuft beim Wischen langsamer', rows: [
      ['amount', 'Stärke', 0, 60, 1, '%']] },
    { g: 'settle', name: 'Einrasten', hint: 'Flasche ruckt nach dem Loslassen', rows: [
      ['dist', 'Ruck', 0, 60, 2, 'px'], ['bounce', 'Nachschwingen', 0, 60, 5, '%']] },
    { g: 'tilt', name: 'Maus', hint: 'Flasche folgt, nur Desktop', rows: [
      ['deg', 'Weg', 0, 30, 1, 'px']] },
    { g: 'ovin', name: 'Auftritt', hint: 'Grafik mit Text: Schrift und Grafik finden zueinander', replay: true,
      sel: ['style', 'Art', [['rise', 'Schrift steigt, Grafik setzt sich'], ['depth', 'Aus der Tiefe'], ['fog', 'Beschlagen, dann klar'], ['slide', 'Schrift gleitet von der Seite']]],
      rows: [['zoom', 'Grafik Zoom', 0, 15, 1, '%'], ['dist', 'Weg der Schrift', 0, 80, 2, 'px'], ['dur', 'Dauer', 300, 2000, 50, 'ms'],
        ['bounce', 'Nachschwingen', 0, 60, 5, '%'], ['step', 'Abstand Zeilen', 0, 250, 10, 'ms']],
      checks: [['again', 'jedes Mal, beim Scrollen und Wischen']] },
    { g: 'ovscroll', name: 'Tiefe Scrollen', hint: 'Grafik bleibt zurück, Schrift läuft voraus', rows: [
      ['img', 'Grafik', 0, 40, 1, '%'], ['txt', 'Schrift', 0, 30, 1, '%'], ['soft', 'Weichheit', 0, 95, 5, '%']] },
    { g: 'ovswipe', name: 'Tiefe Wischen', hint: 'Schrift schneller als die Grafik', rows: [
      ['txt', 'Schrift', 0, 40, 1, '%']] },
    { g: 'ovmouse', name: 'Maus Grafik', hint: 'Schrift und Grafik weichen aus, nur Desktop', rows: [
      ['px', 'Weg', 0, 30, 1, 'px']] },
  ];

  const describe = (cfg) => CONTROLS.map((c) => {
    const v = cfg[c.g] || {};
    const rows = c.rows.map((r) => `${r[1]} ${v[r[0]]} ${r[5]}`).join(', ');
    const dir = c.g === 'arrive' && v.dir ? `, Richtung ${v.dir}` : '';
    const sel = c.sel ? `, ${c.sel[1]} ${(c.sel[2].find((o) => o[0] === v[c.sel[0]]) || ['', v[c.sel[0]]])[1]}` : '';
    const checks = c.checks ? c.checks.map(([k, l]) => `, ${l} ${v[k] ? 'ja' : 'nein'}`).join('') : '';
    return `${c.name}: ${v.on ? 'an' : 'aus'}, ${rows}${dir}${sel}${checks}`;
  }).join('\n');

  const buildRegler = (root, getCfg, setCfg, base, replay) => {
    if (document.querySelector('.zlt-regler')) return;
    const box = document.createElement('div');
    box.className = 'zlt-regler';
    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'zlt-regler__head';
    head.setAttribute('aria-expanded', 'false');
    head.innerHTML = '<span>Regler Teaser</span><span aria-hidden="true">+</span>';
    const body = document.createElement('div');
    body.className = 'zlt-regler__body';
    box.append(head, body);
    head.addEventListener('click', () => {
      const open = box.classList.toggle('is-open');
      head.setAttribute('aria-expanded', String(open));
      head.lastElementChild.textContent = open ? '–' : '+';
    });

    const save = (cfg, replayNow) => {
      writeStore(cfg);
      setCfg(cfg, { replay: replayNow });
    };

    const draw = () => {
      body.innerHTML = '';
      const cfg = getCfg();
      if (!motionOk()) {
        const note = document.createElement('p');
        note.className = 'zlt-regler__note';
        note.textContent = 'Bewegung reduzieren ist eingeschaltet, Effekte sind aus.';
        body.append(note);
      }
      CONTROLS.forEach((c) => {
        const v = cfg[c.g] || (cfg[c.g] = { on: false });
        const group = document.createElement('div');
        group.className = 'zlt-regler__group';
        const check = document.createElement('label');
        check.className = 'zlt-regler__check';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = !!v.on;
        const again = c.g === 'arrive' || c.g === 'text' || !!c.replay;
        cb.addEventListener('change', () => { v.on = cb.checked; save(cfg, again); });
        const name = document.createElement('span');
        name.textContent = `${c.name} `;
        const hint = document.createElement('span');
        hint.className = 'zlt-regler__note';
        hint.textContent = c.hint;
        check.append(cb, name, hint);
        group.append(check);
        c.rows.forEach(([key, label, min, max, step, unit]) => {
          const row = document.createElement('div');
          row.className = 'zlt-regler__row';
          const id = `zlt-${c.g}-${key}`;
          const l = document.createElement('label');
          l.htmlFor = id;
          l.textContent = label;
          const input = document.createElement('input');
          Object.assign(input, { type: 'range', id, min, max, step, value: v[key] });
          const out = document.createElement('output');
          out.htmlFor = id;
          out.textContent = `${v[key]} ${unit}`;
          input.addEventListener('input', () => {
            v[key] = Number(input.value);
            out.textContent = `${v[key]} ${unit}`;
            save(cfg, false);
          });
          input.addEventListener('change', () => save(cfg, again));
          row.append(l, input, out);
          group.append(row);
        });
        if (c.sel) {
          const [key, label, options] = c.sel;
          const row = document.createElement('div');
          row.className = 'zlt-regler__row';
          const l = document.createElement('label');
          l.htmlFor = `zlt-${c.g}-${key}`;
          l.textContent = label;
          const sel = document.createElement('select');
          sel.id = `zlt-${c.g}-${key}`;
          options.forEach(([val, txt]) => {
            const o = document.createElement('option');
            o.value = val;
            o.textContent = txt;
            if (v[key] === val) o.selected = true;
            sel.append(o);
          });
          sel.addEventListener('change', () => { v[key] = sel.value; save(cfg, true); });
          row.append(l, sel);
          group.insertBefore(row, group.children[1] || null);
        }
        (c.checks || []).forEach(([key, label]) => {
          const row = document.createElement('label');
          row.className = 'zlt-regler__check';
          const box2 = document.createElement('input');
          box2.type = 'checkbox';
          box2.checked = !!v[key];
          box2.addEventListener('change', () => { v[key] = box2.checked; save(cfg, false); });
          const t = document.createElement('span');
          t.textContent = label;
          row.append(box2, t);
          group.append(row);
        });
        if (c.g === 'arrive') {
          const row = document.createElement('div');
          row.className = 'zlt-regler__row';
          const l = document.createElement('label');
          l.htmlFor = 'zlt-arrive-dir';
          l.textContent = 'Richtung';
          const sel = document.createElement('select');
          sel.id = 'zlt-arrive-dir';
          [['', 'wie je Folie eingestellt'], ['up', 'von unten'], ['drop', 'von oben, setzt auf'], ['land', 'aus der Nähe']].forEach(([val, txt]) => {
            const o = document.createElement('option');
            o.value = val;
            o.textContent = txt;
            if ((v.dir || '') === val) o.selected = true;
            sel.append(o);
          });
          sel.addEventListener('change', () => { v.dir = sel.value; save(cfg, true); });
          row.append(l, sel);
          group.append(row);
        }
        body.append(group);
      });

      const buttons = document.createElement('div');
      buttons.className = 'zlt-regler__buttons';
      const mk = (text, fn) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = text;
        b.addEventListener('click', fn);
        buttons.append(b);
        return b;
      };
      mk('Abspielen', replay);
      const copy = mk('Werte kopieren', () => {
        const text = describe(getCfg());
        const done = () => { copy.textContent = 'Kopiert'; setTimeout(() => { copy.textContent = 'Werte kopieren'; }, 1500); };
        // Ohne Zwischenablage: Werte als Text zeigen, zum Markieren und Kopieren von Hand
        const fallback = () => {
          let area = body.querySelector('textarea');
          if (!area) {
            area = document.createElement('textarea');
            area.readOnly = true;
            area.rows = CONTROLS.length;
            area.style.width = '100%';
            body.append(area);
          }
          area.value = text;
          area.select();
        };
        if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, fallback);
        else fallback();
      });
      mk('Zurücksetzen', () => {
        writeStore(null);
        setCfg(merge(base, null), { replay: true });
        draw();
      });
      body.append(buttons);
      const note = document.createElement('p');
      note.className = 'zlt-regler__note';
      note.textContent = 'Werte bleiben in diesem Browser gespeichert. Für alle gelten sie erst, wenn sie im Theme-Editor stehen.';
      body.append(note);
    };
    draw();
    document.body.append(box);
  };

  const initAll = () => document.querySelectorAll('[data-zl-teaser-v2]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-zl-teaser-v2]').forEach(init);
  });
})();
